import React, { useState, useEffect } from "react";
import { Menu as MenuIcon, X, Globe } from "lucide-react";
import { useI18n } from "../i18n";
import { WHATSAPP_URL } from "../constants";

const Logo = () => (
  <a
    href="#top"
    className="flex items-center gap-3 group"
    data-testid="nav-logo"
  >
    <span className="tinta-badge">T.</span>
    <div className="leading-none">
      <div className="font-serif-display text-xl tracking-wide text-[#f4f0eb]">
        TINTA
      </div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#a39e97] mt-0.5">
        Restaurante & Bar
      </div>
    </div>
  </a>
);

export default function Navbar() {
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t.nav.about, id: "about" },
    { href: "#menu", label: t.nav.menu, id: "menu" },
    { href: "#gallery", label: t.nav.gallery, id: "gallery" },
    { href: "#testimonials", label: t.nav.testimonials, id: "testimonials" },
    { href: "#contact", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0d0c0b]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              className="text-[13px] uppercase tracking-[0.22em] text-[#a39e97] hover:text-[#d4af37] transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="hidden sm:flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#a39e97] hover:text-[#d4af37] transition-colors border border-white/10 px-3 py-2 rounded-none"
            data-testid="lang-toggle-button"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            <span>{lang === "es" ? "EN" : "ES"}</span>
          </button>

          <a
            href={WHATSAPP_URL(
              lang === "es"
                ? "Hola TINTA, me gustaría hacer una reserva."
                : "Hi TINTA, I'd like to make a reservation."
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex tinta-btn-primary"
            data-testid="nav-reserve-button"
          >
            {t.nav.reserve}
          </a>

          <button
            className="lg:hidden text-[#f4f0eb] p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden bg-[#0d0c0b]/95 backdrop-blur-xl border-t border-white/5"
          data-testid="mobile-menu-panel"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-[14px] uppercase tracking-[0.22em] text-[#a39e97] hover:text-[#d4af37] transition-colors"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                toggle();
                setOpen(false);
              }}
              className="flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#a39e97] hover:text-[#d4af37] transition-colors w-fit"
              data-testid="mobile-lang-toggle"
            >
              <Globe size={14} />
              {lang === "es" ? "English" : "Español"}
            </button>
            <a
              href={WHATSAPP_URL(
                lang === "es"
                  ? "Hola TINTA, me gustaría hacer una reserva."
                  : "Hi TINTA, I'd like to make a reservation."
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="tinta-btn-primary w-full"
              data-testid="mobile-reserve-button"
            >
              {t.nav.reserve}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
